import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Sparkles, Pencil, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatIDR, formatDate } from "@/lib/format";
import { PageHeader, Card, Loading, Button, Modal, Field, TierBadge, StatCard, SeverityTag } from "@/components/kit";
import MemberCard from "@/components/MemberCard";

const EVENT_COLOR = { EARN: "#7CD992", REDEEM: "#FF8080", ADJUSTMENT: "#E7C25B", OPENING: "#8FD3E8" };

export default function CustomerProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const base = user.role === "owner" ? "/owner" : "/admin";
  const [member, setMember] = useState(null);
  const [history, setHistory] = useState(null);
  const [txns, setTxns] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdjust, setShowAdjust] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, h, t, ms] = await Promise.all([
        api.getMember(id),
        api.pointsHistory(id).catch(() => null),
        api.getTransactions(id).catch(() => []),
        api.memberMemberships(id).catch(() => []),
      ]);
      setMember(m); setHistory(h); setTxns(t); setMemberships(ms);
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <Loading />;
  if (!member) return <Card>Member tidak ditemukan.</Card>;

  return (
    <div>
      <button onClick={() => navigate(`${base}/members`)} className="mb-4 inline-flex items-center gap-2 text-sm text-[#8E8E93] hover:text-white">
        <ArrowLeft className="h-4 w-4" /> Kembali
      </button>

      <PageHeader
        eyebrow="Profil Member"
        title={member.name}
        subtitle={member.phone || "Tanpa nomor HP"}
        right={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setShowEdit(true)} data-testid="edit-member"><Pencil className="h-4 w-4" /> Edit</Button>
            <Button variant="outline" onClick={() => setShowPwd(true)} data-testid="reset-pwd"><KeyRound className="h-4 w-4" /> Reset Password</Button>
          </div>
        }
      />

      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <div className="hsc-card p-5"><div className="hsc-label">Tier</div><div className="mt-2"><TierBadge tier={member.tier} /></div></div>
        <StatCard label="Total Belanja" value={formatIDR(member.lifetime_spend)} />
        <StatCard label="Poin" value={member.points} accent="#E7C25B" />
        <StatCard label="Kunjungan" value={member.visits || 0} />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <div className="mb-4 flex items-center justify-between">
            <div className="hsc-label">Riwayat Poin (Ledger)</div>
            <Button variant="outline" onClick={() => setShowAdjust(true)} data-testid="adjust-points"><Sparkles className="h-4 w-4" /> Sesuaikan</Button>
          </div>
          {history && (
            <div className="mb-3 flex items-center gap-2 text-xs">
              <span className="text-[#6E6E73]">Rekonsiliasi:</span>
              <SeverityTag value={history.reconciled ? "PAID" : "WARNING"} />
              <span className="text-[#8E8E93]">Saldo {history.current_balance} pts</span>
            </div>
          )}
          <div className="max-h-96 space-y-2 overflow-y-auto pr-1">
            {(history?.events || []).map((e, i) => (
              <div key={i} className="flex items-center justify-between border-b border-[#141416] py-2 text-sm" data-testid="points-event">
                <div>
                  <div className="text-white">{e.label} <span className="text-[11px] text-[#6E6E73]">· {e.description}</span></div>
                  <div className="text-[11px] text-[#5A5A5F]">{formatDate(e.date)} · saldo {e.balance_after}</div>
                </div>
                <div className="font-medium" style={{ color: EVENT_COLOR[e.type] || "#fff" }}>
                  {e.points_delta > 0 ? "+" : ""}{e.points_delta}
                </div>
              </div>
            ))}
            {!(history?.events || []).length && <div className="py-6 text-center text-sm text-[#6E6E73]">Belum ada aktivitas poin.</div>}
          </div>
        </Card>

        <div className="space-y-6">
          <Card>
            <div className="mb-4 hsc-label">Member Card</div>
            <MemberCard member={member} memberships={memberships} />
          </Card>

          <Card>
            <div className="mb-4 hsc-label">Membership</div>
            {!memberships.length ? <div className="text-sm text-[#6E6E73]">Tidak ada membership aktif.</div> : (
              <div className="space-y-2">
                {memberships.map((m, i) => (
                  <div key={i} className="flex items-center justify-between rounded-lg border border-[#1F1F22] px-3 py-2 text-sm">
                    <span className="text-white">{m.category}</span>
                    <div className="flex items-center gap-2">
                      <SeverityTag value={m.status} />
                      <span className="text-[11px] text-[#6E6E73]">{m.days_left}h</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card>
            <div className="mb-4 hsc-label">Transaksi Terakhir</div>
            <div className="max-h-72 space-y-2 overflow-y-auto pr-1">
              {txns.slice(0, 20).map((t) => (
                <div key={t.id} className="flex items-center justify-between border-b border-[#141416] py-2 text-sm">
                  <div>
                    <div className="text-white">{t.category}</div>
                    <div className="text-[11px] text-[#5A5A5F]">{formatDate(t.transaction_at || t.created_at)}</div>
                  </div>
                  <div className="text-[#D1D1D6]">{formatIDR(t.amount)}</div>
                </div>
              ))}
              {!txns.length && <div className="py-6 text-center text-sm text-[#6E6E73]">Belum ada transaksi.</div>}
            </div>
          </Card>
        </div>
      </div>

      {showAdjust && <AdjustModal id={id} onClose={() => setShowAdjust(false)} onSaved={() => { setShowAdjust(false); load(); }} />}
      {showEdit && <EditMemberModal member={member} onClose={() => setShowEdit(false)} onSaved={() => { setShowEdit(false); load(); }} />}
      {showPwd && <PwdModal id={id} onClose={() => setShowPwd(false)} />}
    </div>
  );
}

function AdjustModal({ id, onClose, onSaved }) {
  const [delta, setDelta] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!Number(delta)) return toast.error("Nilai tidak boleh 0");
    if (!reason.trim()) return toast.error("Alasan wajib diisi");
    setBusy(true);
    try { const r = await api.addPointsAdjustment(id, { points_delta: Number(delta), reason: reason.trim() }); toast.success(`Poin baru: ${r.new_points}`); onSaved(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Modal open title="Penyesuaian Poin Manual" onClose={onClose}>
      <div className="space-y-4">
        <Field label="Jumlah Poin (boleh negatif)"><input type="number" className="hsc-input" value={delta} onChange={(e) => setDelta(e.target.value)} placeholder="mis. 50 atau -20" data-testid="adjust-delta" /></Field>
        <Field label="Alasan"><input className="hsc-input" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="adjust-reason" /></Field>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>Batal</Button>
          <Button onClick={save} disabled={busy} data-testid="adjust-save">Simpan</Button>
        </div>
      </div>
    </Modal>
  );
}

function EditMemberModal({ member, onClose, onSaved }) {
  const [name, setName] = useState(member.name);
  const [phone, setPhone] = useState(member.phone || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.updateMember(member.id, { name: name.trim(), phone: phone.trim() }); toast.success("Tersimpan"); onSaved(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Modal open title="Edit Member" onClose={onClose}>
      <div className="space-y-4">
        <Field label="Nama"><input className="hsc-input" value={name} onChange={(e) => setName(e.target.value)} /></Field>
        <Field label="Nomor HP"><input className="hsc-input" value={phone} onChange={(e) => setPhone(e.target.value)} /></Field>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>Batal</Button>
          <Button onClick={save} disabled={busy}>Simpan</Button>
        </div>
      </div>
    </Modal>
  );
}

function PwdModal({ id, onClose }) {
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (pwd.length < 6) return toast.error("Minimal 6 karakter");
    setBusy(true);
    try { await api.resetMemberPassword(id, pwd); toast.success("Password diperbarui"); onClose(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Modal open title="Reset Password Member" onClose={onClose}>
      <div className="space-y-4">
        <Field label="Password Baru"><input type="password" className="hsc-input" value={pwd} onChange={(e) => setPwd(e.target.value)} /></Field>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>Batal</Button>
          <Button onClick={save} disabled={busy}>Simpan</Button>
        </div>
      </div>
    </Modal>
  );
}
